import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  useTheme,
  alpha
} from '@mui/material';
import {
  LocalPharmacy as MedicationIcon,
  Image as ImagingIcon,
  Science as LabIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Cancel as RejectedIcon,
  History as HistoryIcon
} from '@mui/icons-material';

/**
 * PrescriptionHistory Component
 * 
 * This component displays the prescription history for a patient.
 * 
 * @param {Object} props
 * @param {string} props.patientId - The ID of the patient
 * @param {Array} props.prescriptions - Optional array of prescriptions to display
 */
const PrescriptionHistory = ({ patientId, prescriptions: propPrescriptions }) => {
  const theme = useTheme();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Fetch prescriptions when the component mounts
  useEffect(() => {
    const fetchPrescriptions = async () => {
      // If prescriptions are provided as props, use them
      if (propPrescriptions) {
        setPrescriptions(propPrescriptions);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // In a real implementation, this would be an API call to fetch prescriptions
        // For now, we'll use mock data
        setTimeout(() => {
          // Mock data for demonstration
          const mockPrescriptions = [
            {
              id: '1',
              type: 'medication',
              medication: 'Amoxicillin',
              dosage: '500mg',
              frequency: 'Three times daily',
              duration: '7 days',
              notes: 'Take with food to reduce stomach upset',
              createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
              status: 'completed',
              doctorName: 'Dr. Sarah Wilson'
            },
            {
              id: '2',
              type: 'imaging',
              imagingType: 'X-ray',
              bodyPart: 'Chest',
              notes: 'Check for pneumonia',
              createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago
              status: 'completed',
              doctorName: 'Dr. Sarah Wilson',
              results: 'No significant findings. Lungs clear.'
            },
            {
              id: '3',
              type: 'lab',
              tests: ['Complete Blood Count (CBC)', 'Basic Metabolic Panel'],
              notes: 'Routine check',
              createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
              status: 'completed',
              doctorName: 'Dr. Sarah Wilson',
              results: 'All values within normal range.'
            },
            {
              id: '4',
              type: 'medication',
              medication: 'Ibuprofen',
              dosage: '400mg',
              frequency: 'As needed for pain',
              duration: 'Up to 5 days',
              notes: 'Do not exceed 1200mg in 24 hours',
              createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
              status: 'pending',
              doctorName: 'Dr. Sarah Wilson'
            }
          ];
          
          setPrescriptions(mockPrescriptions);
          setLoading(false);
        }, 1000);
      } catch (err) {
        console.error('Error fetching prescriptions:', err);
        setError('Failed to load prescription history. Please try again.');
        setLoading(false);
      }
    };
    
    fetchPrescriptions();
  }, [patientId, propPrescriptions]);

  // Toggle expanded state for a prescription
  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get icon based on prescription type
  const getPrescriptionIcon = (type) => {
    switch (type) {
      case 'medication':
        return <MedicationIcon />;
      case 'imaging':
        return <ImagingIcon />;
      case 'lab':
        return <LabIcon />;
      default:
        return null;
    }
  };

  // Get status chip based on prescription status
  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon />}
            label="Pending"
            size="small"
            color="warning"
            variant="outlined"
          />
        );
      case 'completed':
        return (
          <Chip
            icon={<ApprovedIcon />}
            label="Completed"
            size="small"
            color="success"
            variant="outlined"
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<RejectedIcon />}
            label="Rejected"
            size="small"
            color="error"
            variant="outlined"
          />
        );
      default:
        return null;
    }
  };

  // Get prescription details based on type
  const getPrescriptionDetails = (prescription) => {
    switch (prescription.type) {
      case 'medication':
        return (
          <>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Medication:</strong> {prescription.medication}
            </Typography>
            <Typography variant="body2">
              <strong>Dosage:</strong> {prescription.dosage}
            </Typography>
            <Typography variant="body2">
              <strong>Frequency:</strong> {prescription.frequency}
            </Typography>
            {prescription.duration && (
              <Typography variant="body2">
                <strong>Duration:</strong> {prescription.duration}
              </Typography>
            )}
            {prescription.notes && (
              <Typography variant="body2">
                <strong>Instructions:</strong> {prescription.notes}
              </Typography>
            )}
          </>
        );
      case 'imaging':
        return (
          <>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Imaging Type:</strong> {prescription.imagingType}
            </Typography>
            <Typography variant="body2">
              <strong>Body Part:</strong> {prescription.bodyPart}
            </Typography>
            {prescription.notes && (
              <Typography variant="body2">
                <strong>Clinical Indication:</strong> {prescription.notes}
              </Typography>
            )}
            {prescription.results && (
              <Typography variant="body2">
                <strong>Results:</strong> {prescription.results}
              </Typography>
            )}
          </>
        );
      case 'lab':
        return (
          <>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Tests:</strong> {prescription.tests.join(', ')}
            </Typography>
            {prescription.notes && (
              <Typography variant="body2">
                <strong>Clinical Indication:</strong> {prescription.notes}
              </Typography>
            )}
            {prescription.results && (
              <Typography variant="body2">
                <strong>Results:</strong> {prescription.results}
              </Typography>
            )}
          </>
        );
      default:
        return null;
    }
  };

  if (loading && prescriptions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading prescription history...
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 0 }}>
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1 }} />
          Prescription History
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <List sx={{ p: 0 }}>
        {prescriptions.length === 0 ? (
          <ListItem>
            <ListItemText 
              primary="No prescription history" 
              secondary="No prescriptions have been created for this patient yet"
              sx={{ textAlign: 'center', py: 2 }}
            />
          </ListItem>
        ) : (
          prescriptions.map((prescription, index) => (
            <React.Fragment key={prescription.id}>
              {index > 0 && <Divider />}
              <ListItem 
                alignItems="flex-start"
                sx={{ 
                  py: 2,
                  bgcolor: expandedItems[prescription.id] ? alpha(theme.palette.primary.main, 0.05) : 'transparent'
                }}
              >
                <Box sx={{ display: 'flex', width: '100%' }}>
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    {getPrescriptionIcon(prescription.type)}
                  </Box>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {prescription.type === 'medication' && prescription.medication}
                        {prescription.type === 'imaging' && `${prescription.imagingType} - ${prescription.bodyPart}`}
                        {prescription.type === 'lab' && 'Laboratory Tests'}
                      </Typography>
                      
                      {getStatusChip(prescription.status)}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(prescription.createdAt)} • {prescription.doctorName}
                    </Typography>
                    
                    <Collapse in={expandedItems[prescription.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 2, mb: 1 }}>
                        {getPrescriptionDetails(prescription)}
                      </Box>
                    </Collapse>
                  </Box>
                  
                  <Box>
                    <Tooltip title={expandedItems[prescription.id] ? "Show less" : "Show more"}>
                      <IconButton onClick={() => toggleExpanded(prescription.id)}>
                        {expandedItems[prescription.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListItem>
            </React.Fragment>
          ))
        )}
      </List>
    </Paper>
  );
};

export default PrescriptionHistory; 